import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormUpload, FormUploadService } from './form-upload.service';
import { I18nService } from '../../core/i18n.service';

const UPLOAD_COPY={
  tr:{video:'Video yükle',image:'Görsel yükle',minimum:'en az',loading:'Yükleniyor...',remove:'Dosyayı sil',failed:'Dosya yüklenemedi.'},
  en:{video:'Upload video',image:'Upload image',minimum:'at least',loading:'Uploading...',remove:'Remove file',failed:'The file could not be uploaded.'},
  de:{video:'Video hochladen',image:'Bild hochladen',minimum:'mindestens',loading:'Wird hochgeladen...',remove:'Datei entfernen',failed:'Die Datei konnte nicht hochgeladen werden.'},
  es:{video:'Subir vídeo',image:'Subir imagen',minimum:'mínimo',loading:'Subiendo...',remove:'Eliminar archivo',failed:'No se pudo subir el archivo.'},
  fr:{video:'Téléverser une vidéo',image:'Téléverser une image',minimum:'au moins',loading:'Téléversement...',remove:'Supprimer le fichier',failed:'Impossible de téléverser le fichier.'}
} as const;

@Component({
  selector: 'app-form-upload-field',
  standalone: true,
  template: `
    <label class="upload-field" [class.uploading]="uploading">
      <input type="file" [accept]="accept" [multiple]="maxFiles > 1" [disabled]="uploading || value.length >= maxFiles" (change)="select($event)" />
      <span>{{ fieldType === 'video' ? copy().video : copy().image }}</span>
      <small>{{ value.length }}/{{ maxFiles }}{{ minFiles > 0 ? ' · ' + copy().minimum + ' ' + minFiles : '' }}</small>
      @if (uploading) { <em>{{copy().loading}}</em> }
    </label>
    @if (value.length) {
      <div class="upload-previews">
        @for (file of value; track fileKey(file)) {
          <figure>
            @if (file.isImage && file.tempFile) { <img [src]="file.tempFile" [alt]="file.fileName" /> }
            <figcaption>{{ file.fileName }}</figcaption>
            <button type="button" (click)="remove(file)" [attr.aria-label]="copy().remove">×</button>
          </figure>
        }
      </div>
    }
  `,
  styles: [`
    .upload-field{align-items:center;border:1px dashed var(--border);border-radius:16px;cursor:pointer;display:flex;gap:12px;padding:13px 14px}
    .upload-field input{display:none}.upload-field span{font-weight:800}.upload-field small,.upload-field em{color:var(--muted);font-size:12px;font-style:normal}.upload-field em{color:var(--accent)}
    .upload-previews{display:flex;flex-wrap:wrap;gap:10px;margin-top:10px}.upload-previews figure{align-items:center;background:color-mix(in srgb,var(--paper) 72%,transparent);border:1px solid var(--border);border-radius:14px;display:grid;gap:6px;grid-template-columns:auto 1fr auto;margin:0;padding:7px}.upload-previews img{border-radius:9px;height:54px;object-fit:cover;width:54px}.upload-previews figcaption{font-size:12px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.upload-previews button{background:transparent;border:1px solid var(--border);border-radius:999px;color:var(--ink);cursor:pointer;font:inherit;font-weight:900;height:28px;line-height:1;width:28px}
  `]
})
export class FormUploadFieldComponent {
  private readonly uploader = inject(FormUploadService);
  private readonly i18n = inject(I18nService);
  protected copy(){return UPLOAD_COPY[this.i18n.lang()]||UPLOAD_COPY.en;}
  @Input() fieldKey = '';
  @Input() fieldType = 'image';
  @Input() endpoint = '/upload/form';
  @Input() maxFiles = 1;
  @Input() minFiles = 0;
  @Input() value: any[] = [];
  @Input() uploading = false;
  @Output() valueChange = new EventEmitter<FormUpload[]>();
  @Output() uploadState = new EventEmitter<boolean>();
  @Output() uploadError = new EventEmitter<string>();

  get accept(): string { return this.fieldType === 'video' ? 'video/*' : 'image/*'; }

  async select(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []).slice(0, Math.max(this.maxFiles - this.value.length, 0));
    if (!selected.length) { input.value = ''; return; }
    this.uploadState.emit(true);
    try {
      const uploaded = [...this.value] as FormUpload[];
      for (const file of selected) uploaded.push(await this.uploader.upload(this.fieldKey, file, this.endpoint));
      this.valueChange.emit(uploaded);
    } catch (error) {
      this.uploadError.emit(error instanceof Error ? error.message : this.copy().failed);
    } finally {
      this.uploadState.emit(false);
      input.value = '';
    }
  }

  remove(file: any): void {
    const tempFile = String(file['tempFile'] ?? '');
    if (/^blob:/i.test(tempFile) && typeof URL !== 'undefined') URL.revokeObjectURL(tempFile);
    this.valueChange.emit(this.value.filter((item) => item !== file));
  }

  fileKey(file: any): string { return `${file['uploadID'] ?? file['id']}-${file['fileName'] ?? file['name']}`; }
}
